import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Clock, FileText, MessageSquare, Loader2, ImagePlus, Trash2 } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { useLang } from "@/context/LanguageContext";
import api, { API, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function Dashboard() {
  const { t, lang } = useLang();
  const [appn, setAppn] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/applications/mine").then(({ data }) => setAppn(data)).finally(() => setLoading(false));
  }, []);

  const editable = appn && (appn.status === "Draft" || appn.status === "More Information Required");
  const groomName = appn?.groom?.full_name;
  const brideName = appn?.bride?.full_name;

  return (
    <div className="min-h-screen flex flex-col bg-[#FAF6F0]">
      <Navbar />
      <main className="flex-1 max-w-5xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <h1 className="font-serif text-3xl sm:text-4xl text-[#8B1E2B]" data-testid="dashboard-title">{t("nav_dashboard")}</h1>

        {loading ? (
          <div className="mt-10 flex items-center gap-2 text-slate-500"><Loader2 className="h-5 w-5 animate-spin" /> …</div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mt-6 grid gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2 space-y-6">
              <div className="rounded-2xl bg-white border border-amber-500/20 p-6 shadow-sm">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <div className="text-xs text-slate-500">{t("my_status")}</div>
                    <div className="mt-2"><StatusBadge status={appn.status} lang={lang} /></div>
                  </div>
                  <Link to="/apply" data-testid="dashboard-apply-button">
                    <Button className="bg-[#8B1E2B] hover:bg-[#6f1622] text-white">
                      {appn.status === "Draft" ? t("start_app") : editable ? t("continue_app") : t("view_app")}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </div>
                {(groomName || brideName) && (
                  <div className="mt-5 grid sm:grid-cols-2 gap-4">
                    <SummaryLine label={t("step_groom")} value={groomName} />
                    <SummaryLine label={t("step_bride")} value={brideName} />
                  </div>
                )}
                {appn.decision?.reason && (
                  <div className="mt-5 rounded-xl bg-amber-50 border border-amber-300 p-4 text-sm text-amber-900" data-testid="decision-reason">
                    <strong>{appn.decision.outcome}:</strong> {appn.decision.reason}
                  </div>
                )}
              </div>

              {appn.status === "Verified" && (
                <GalleryPrefs appn={appn} setAppn={setAppn} />
              )}

              {appn.review_notes?.length > 0 && (
                <div className="rounded-2xl bg-white border border-amber-500/20 p-6 shadow-sm" data-testid="organiser-notes">
                  <h3 className="font-serif text-xl text-slate-900 flex items-center gap-2"><MessageSquare className="h-5 w-5 text-[#8B1E2B]" /> {t("organiser_notes")}</h3>
                  <ul className="mt-4 space-y-3">
                    {appn.review_notes.map((n) => (
                      <li key={n.id} className="rounded-lg bg-amber-50/60 border border-amber-200 p-3 text-sm text-slate-700">
                        {n.note}
                        <div className="text-xs text-slate-400 mt-1">{new Date(n.at).toLocaleString()}</div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="rounded-2xl bg-white border border-amber-500/20 p-6 shadow-sm">
              <h3 className="font-serif text-xl text-slate-900 flex items-center gap-2"><Clock className="h-5 w-5 text-[#8B1E2B]" /> {t("timeline")}</h3>
              <ol className="mt-4 space-y-4" data-testid="timeline-list">
                {[...(appn.timeline || [])].reverse().map((tl, i) => (
                  <li key={i} className="relative pl-6">
                    <span className="absolute left-0 top-1.5 h-2.5 w-2.5 rounded-full bg-[#8B1E2B]" />
                    <div className="text-sm font-medium text-slate-800">{tl.status}</div>
                    {tl.note && <div className="text-xs text-slate-500">{tl.note}</div>}
                    <div className="text-xs text-slate-400">{new Date(tl.at).toLocaleString()}</div>
                  </li>
                ))}
              </ol>
              <div className="mt-6 flex items-center gap-2 text-xs text-slate-400">
                <FileText className="h-4 w-4" /> {(appn.documents || []).filter((d) => !d.is_deleted).length} document(s)
              </div>
            </div>
          </motion.div>
        )}
      </main>
      <Footer />
    </div>
  );
}

function SummaryLine({ label, value }) {
  return (
    <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="font-medium text-slate-900">{value || "—"}</div>
    </div>
  );
}

function GalleryPrefs({ appn, setAppn }) {
  const { t } = useLang();
  const inputRef = useRef();
  const [busy, setBusy] = useState(false);
  const photoDoc = (appn.documents || []).find((d) => d.category === "gallery_photo" && !d.is_deleted);

  const savePrefs = async (patch) => {
    try {
      const { data } = await api.put("/applications/mine/gallery-prefs", patch);
      setAppn((p) => ({ ...p, ...data }));
    } catch {
      toast.error("Could not update your preference.");
    }
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("category", "gallery_photo");
    fd.append("file", file);
    try {
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { data } = await api.get("/applications/mine");
      setAppn(data);
      toast.success("Photo added");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const removePhoto = async () => {
    if (!photoDoc) return;
    try {
      await api.delete(`/documents/${photoDoc.id}`);
      const { data } = await api.get("/applications/mine");
      setAppn(data);
      toast.success("Photo removed");
    } catch {
      toast.error("Could not remove photo");
    }
  };

  return (
    <div className="rounded-2xl bg-white border border-emerald-300 p-6 shadow-sm space-y-5" data-testid="publish-consent-card">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-serif text-xl text-slate-900">{t("publish_consent_title")}</h3>
          <p className="mt-1 text-sm text-slate-600">{t("publish_consent_desc")}</p>
        </div>
        <Switch checked={!!appn.publish_consent} onCheckedChange={(v) => savePrefs({ publish_consent: !!v })} data-testid="publish-consent-switch" />
      </div>

      {appn.publish_consent && (
        <>
          <div className="border-t border-slate-100 pt-4">
            <Label className="text-sm text-slate-700">{t("name_display_label")}</Label>
            <RadioGroup value={appn.gallery_name_display || "first"} onValueChange={(v) => savePrefs({ name_display: v })} className="mt-2 flex gap-6" data-testid="name-display-group">
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <RadioGroupItem value="first" data-testid="name-display-first" /> {t("name_first_only")}
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <RadioGroupItem value="full" data-testid="name-display-full" /> {t("name_full")}
              </label>
            </RadioGroup>
          </div>

          <div className="border-t border-slate-100 pt-4">
            <h4 className="font-semibold text-slate-800 text-sm">{t("photo_title")}</h4>
            <p className="text-xs text-slate-500 mt-0.5">{t("photo_desc")}</p>
            <div className="mt-3 flex items-center gap-4 flex-wrap">
              {photoDoc && (
                <img src={`${API}/gallery/${appn.id}/photo?t=${Date.now()}`} alt="Celebration" className="h-20 w-20 rounded-lg object-cover border border-amber-300" data-testid="gallery-photo-preview" onError={(e) => { e.currentTarget.style.display = "none"; }} />
              )}
              <input ref={inputRef} type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden" data-testid="gallery-photo-input" onChange={(e) => uploadPhoto(e.target.files?.[0])} />
              <Button size="sm" variant="outline" disabled={busy} onClick={() => inputRef.current?.click()} data-testid="gallery-photo-upload-button" className="border-amber-500/40 text-[#8B1E2B]">
                {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <ImagePlus className="h-4 w-4 mr-1.5" />} {photoDoc ? "Replace" : t("upload_photo")}
              </Button>
              {photoDoc && (
                <button onClick={removePhoto} className="p-2 rounded hover:bg-red-100 text-red-600" data-testid="gallery-photo-remove" aria-label="Remove photo"><Trash2 className="h-4 w-4" /></button>
              )}
            </div>
            {photoDoc && (
              <label className="mt-3 flex items-center gap-3 cursor-pointer">
                <Switch checked={!!appn.photo_consent} onCheckedChange={(v) => savePrefs({ photo_consent: !!v })} data-testid="photo-consent-switch" />
                <span className="text-sm text-slate-700">{t("show_my_photo")}</span>
              </label>
            )}
          </div>
        </>
      )}
    </div>
  );
}
